import { JSONContent } from '@tiptap/core';

import { ContatoMencionado } from 'types';
import { isMencaoTodos, MENCAO_TODOS } from 'utils/mencaoTodos';

export interface TiptapSerializeResult {
  text: string;
  mentions: ContatoMencionado[];
}

type MarkType = { type: string };

function serializeNodes(
  nodes: JSONContent[],
  text: { current: string },
  mentions: ContatoMencionado[],
  isFirstParagraph: { value: boolean },
) {
  nodes.forEach(node => {
    if (node.type === 'paragraph') {
      if (!isFirstParagraph.value && !text.current.endsWith('\n')) {
        text.current += '\n';
      }
      isFirstParagraph.value = false;
      if (node.content)
        serializeNodes(node.content, text, mentions, isFirstParagraph);
    } else if (node.type === 'text') {
      const rawChunk = node.text ?? '';
      const leadingWhitespace = rawChunk.match(/^\s*/)?.[0] ?? '';
      const trailingWhitespace = rawChunk.trim()
        ? rawChunk.match(/\s*$/)?.[0] ?? ''
        : '';
      let chunk = rawChunk.slice(
        leadingWhitespace.length,
        rawChunk.length - trailingWhitespace.length,
      );
      const marks: MarkType[] = (node.marks as MarkType[]) ?? [];
      if (chunk) {
        marks.forEach((mark: MarkType) => {
          if (mark.type === 'bold') chunk = `*${chunk}*`;
          else if (mark.type === 'italic') chunk = `_${chunk}_`;
          else if (mark.type === 'strike') chunk = `~${chunk}~`;
          else if (mark.type === 'code') chunk = `\`${chunk}\``;
        });
      }
      text.current += `${leadingWhitespace}${chunk}${trailingWhitespace}`;
    } else if (node.type === 'mention') {
      const attrs = node.attrs ?? {};
      const waPhoneNumberId =
        (attrs.waPhoneNumberId as string) ||
        (attrs.contact as string) ||
        (attrs.id as string);
      mentions.push(
        isMencaoTodos(waPhoneNumberId)
          ? MENCAO_TODOS
          : {
              contatoId: attrs.id as string,
              waPhoneNumberId,
              contatoNome: (attrs.label as string) || undefined,
            },
      );
      text.current += `@${waPhoneNumberId}`;
    } else if (node.type === 'hardBreak') {
      text.current += '\n';
    } else if (node.type === 'codeBlock') {
      const inner = (node.content ?? []).map(n => n.text ?? '').join('');
      if (!isFirstParagraph.value && !text.current.endsWith('\n')) {
        text.current += '\n';
      }
      isFirstParagraph.value = false;
      text.current += `\`\`\`\n${inner}\n\`\`\``;
    } else if (node.type === 'blockquote') {
      if (!isFirstParagraph.value && !text.current.endsWith('\n')) {
        text.current += '\n';
      }
      isFirstParagraph.value = false;
      (node.content ?? []).forEach(child => {
        text.current += '> ';
        if (child.content)
          serializeNodes(child.content, text, mentions, { value: true });
        text.current += '\n';
      });
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      if (!isFirstParagraph.value && !text.current.endsWith('\n')) {
        text.current += '\n';
      }
      isFirstParagraph.value = false;
      (node.content ?? []).forEach((li, index) => {
        const listStart = Number(node.attrs?.start) || 1;
        text.current +=
          node.type === 'orderedList' ? `${listStart + index}. ` : '- ';
        if (li.content)
          serializeNodes(li.content, text, mentions, { value: true });
        text.current += '\n';
      });
    }
  });
}

/**
 * Converts a Tiptap editor JSON document to a WhatsApp-formatted string and
 * extracts the list of mentioned contacts for the backend payload.
 *
 * Mention nodes are serialized as `@waPhoneNumberId` so that
 * `renderTextWithMentions` can replace them with the display alias when
 * rendering received messages.
 */
export function tiptapToWhatsapp(doc: JSONContent): TiptapSerializeResult {
  const text = { current: '' };
  const mentions: ContatoMencionado[] = [];
  const isFirstParagraph = { value: true };

  if (doc.content) {
    serializeNodes(doc.content, text, mentions, isFirstParagraph);
  }

  return { text: text.current, mentions };
}
